import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { BentoCategoriesContext } from "contexts/BentoCategories";
import { CampaignsContext } from "contexts/Campaigns";
import { GamificationContext } from "contexts/Gamification";
import { NewCampaignContext } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Campaign,
  CampaignStatus,
  CampaignWritingMode,
  TemplateError,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import { EMAIL_TARGET_FOR_FREE } from "constants/influencer";
import { OrganizationGamificationTask } from "features/Influencer/HomePage/Gamification/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useGamification } from "utils/useGamification";
import { useSubscription } from "utils/useSubscription";

import Header from "./Header";
import Brands from "./Steps/Brands";
import Details from "./Steps/Details";
import Review from "./Steps/Review";
import WriteEmails from "./Steps/WriteEmails";
import {
  CAMPAIGN_BUILDER_BASE_PATH,
  CampaignStep,
  CampaignStepOrders,
  CampaignStepTitles,
  getActiveStep,
} from "./schema";
import styles from "./styles";

interface Props {
  onExit?: (justFinished: boolean) => void;
  // Dialog mode only: open an already-created campaign and jump straight to a step
  // (e.g. local pitching creates the campaign, then jumps to "Write Emails").
  initialCampaignId?: number;
  initialStep?: CampaignStep;
}

export default function CampaignBuilder({
  onExit,
  initialCampaignId,
  initialStep,
}: Props = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    campaign,
    setCampaign,
    setSelectedType,
    setSelectedBrandIds,
    setExcludedBrandIds,
    setSelectAll,
    setSelectedCollection,
    setCurrentSearchParams,
    setPreviousSelectionSnapshot,
    setTemplates,
  } = useContext(NewCampaignContext);
  const { topLevelCategories, fetchBentoCategories } = useContext(
    BentoCategoriesContext,
  );
  const { setCampaigns } = useContext(CampaignsContext);
  const { campaignId } = useParams();
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { decreaseEmailCount } = useSubscription();
  const { completeTask, refreshGamificationProfile } = useGamification();
  const { pitchCount, setPitchCount } = useContext(GamificationContext);
  // In a dialog there is no URL to drive step state, so the builder tracks the
  // active step locally; on the full page it stays URL-backed (deep-linkable,
  // browser back/forward).
  const dialogMode = !!onExit;
  const [dialogStep, setDialogStep] = useState(0);
  const [fetchingCampaign, setFetchingCampaign] = useState(
    !!campaignId || !!initialCampaignId,
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [templateError, setTemplateError] = useState<TemplateError | null>(
    null,
  );
  const activeStep = dialogMode ? dialogStep : getActiveStep(location.pathname);
  const fromParam = dialogMode
    ? "home"
    : new URLSearchParams(location.search).get("from");

  const navigateToStep = (step: CampaignStep, id?: number, replace = false) => {
    if (dialogMode) {
      setDialogStep(CampaignStepOrders.indexOf(step));
      return;
    }
    const effectiveId = id ?? campaign?.id;
    const path = effectiveId
      ? `${CAMPAIGN_BUILDER_BASE_PATH}/campaign/${effectiveId}/${step}`
      : `${CAMPAIGN_BUILDER_BASE_PATH}/new/${step}`;
    // Preserve only the entry-point flag — don't carry brand-search filter
    // params across steps.
    const suffix = fromParam ? `?from=${fromParam}` : "";
    navigate(`${path}${suffix}`, { replace });
  };

  const handleClose = (justFinished: boolean) => {
    if (onExit) {
      if (justFinished) refreshGamificationProfile(true);
      onExit(justFinished);
      return;
    }
    if (fromParam === "local") {
      navigate(`/${routes.pitchLocal}`);
      return;
    }
    if (fromParam === "home") {
      if (justFinished) refreshGamificationProfile(true);
      navigate(`/${routes.home}`);
      return;
    }
    navigate(`/${routes.campaignView}`);
  };

  const hydrateSelection = async (loadedCampaign: Campaign) => {
    if (!currentOrg || !loadedCampaign?.id) return;
    try {
      const { bentoBrandIds } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${loadedCampaign.id}/brand-ids`,
        "GET",
      );
      // Rehydrate as an explicit selection so going back re-shows the exact brands
      // picked, and forward-nav can't re-run a drifting search.
      setSelectedBrandIds(new Set(bentoBrandIds ?? []));
      setExcludedBrandIds(new Set());
      setSelectAll(false);
      if (loadedCampaign.searchParameters) {
        setCurrentSearchParams(loadedCampaign.searchParameters);
      }
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getLastActiveStep = (loaded: Campaign, brandsCount: number) => {
    if (!(loaded?.name && loaded?.startDate)) return CampaignStep.details;
    if (!(brandsCount && brandsCount > 0)) return CampaignStep.select_brands;
    // AI generate/review lives in the Write Emails step; resume there until launch.
    if (loaded?.writingMode === CampaignWritingMode.ai) {
      return CampaignStep.write_email;
    }
    if (
      !(
        loaded?.emailSetting?.emailSettingTemplates &&
        loaded.emailSetting.emailSettingTemplates.length > 0
      )
    ) {
      return CampaignStep.write_email;
    }
    return CampaignStep.review;
  };

  const resetBuilderState = () => {
    setCampaign(null);
    setSelectedType(null);
    setSelectedBrandIds(new Set());
    setExcludedBrandIds(new Set());
    setSelectAll(false);
    setSelectedCollection(null);
    setPreviousSelectionSnapshot(null);
    setTemplates([]);
  };

  const fetchCampaign = async () => {
    if (!campaignId) {
      // Fresh "/new" session: clear any state left over from a prior campaign.
      resetBuilderState();
      navigateToStep(CampaignStep.details, undefined, true);
      return;
    }
    if (!currentOrg) return;
    setFetchingCampaign(true);
    try {
      const {
        campaign: loaded,
        brandsCount,
        campaignBrands,
      } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${campaignId}?per_page=10`,
        "GET",
      );
      setCampaign({
        ...loaded,
        campaignBrandCount: brandsCount,
        campaignBrands,
      });
      // Drop any template selection carried over from a previously-edited
      // campaign so the Write-Emails step re-hydrates from THIS campaign.
      setTemplates([]);
      if (brandsCount && brandsCount > 0) {
        await hydrateSelection(loaded);
      }
      const stepInRoute = CampaignStepOrders.find((step) =>
        location.pathname.includes(step),
      );
      if (loaded?.status !== CampaignStatus.draft) {
        navigateToStep(CampaignStep.review, loaded.id, true);
        return;
      }
      navigateToStep(
        (stepInRoute as CampaignStep) ||
          getLastActiveStep(loaded, brandsCount) ||
          CampaignStep.details,
        loaded?.id,
        true,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchingCampaign(false);
    }
  };

  // Dialog mode: load an existing campaign into context and jump straight to `step`.
  const loadDialogCampaign = async (id: number, step: CampaignStep) => {
    if (!currentOrg) return;
    setFetchingCampaign(true);
    try {
      const {
        campaign: loaded,
        brandsCount,
        campaignBrands,
      } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/campaigns/${id}?per_page=10`,
        "GET",
      );
      setCampaign({
        ...loaded,
        campaignBrandCount: brandsCount,
        campaignBrands,
      });
      setTemplates([]);
      if (brandsCount && brandsCount > 0) {
        await hydrateSelection(loaded);
      }
      setDialogStep(CampaignStepOrders.indexOf(step));
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchingCampaign(false);
    }
  };

  const moveNext = (id?: number, replace = false) => {
    if (activeStep >= CampaignStepOrders.length - 1) {
      handleClose(true);
      return;
    }
    navigateToStep(
      CampaignStepOrders[activeStep + 1],
      id ?? campaign?.id,
      replace,
    );
  };

  const moveBack = () => {
    if (activeStep <= 0) {
      handleClose(false);
      return;
    }
    navigateToStep(CampaignStepOrders[activeStep - 1], campaign?.id);
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex >= activeStep) return;
    navigateToStep(CampaignStepOrders[stepIndex], campaign?.id);
  };

  const handleUpdateCampaign = async (
    body: Map,
    skipMoveNext?: boolean,
  ): Promise<boolean> => {
    try {
      setSubmitLoading(true);
      setTemplateError(null);
      const wasCreate = !campaign?.id;
      const res = await fetcherAuth(
        getToken,
        campaign?.id
          ? `/api/organization/${currentOrg?.id}/campaigns/${campaign?.id}/draft`
          : `/api/organization/${currentOrg?.id}/campaigns/draft`,
        campaign?.id ? "PATCH" : "POST",
        {},
        body,
        true,
      );
      const json = await res?.json().catch(() => null);
      if (!res?.ok || !json) {
        const message =
          json?.message || "Something went wrong. Please reload and try again.";
        setErrorMessage(message);
        // A template sequence validation error pinpoints the offending
        // template, so surface it inline on that field rather than as an alert.
        if (json?.field === "templates" && json?.templateIdx != null) {
          setTemplateError({ idx: json.templateIdx, message });
        } else {
          setErrorAlert({ message });
        }
        return false;
      }
      const { campaign: updatedCampaign } = json;
      const isLaunching =
        body.status === CampaignStatus.in_progress &&
        campaign?.status !== CampaignStatus.in_progress;
      if (isLaunching) {
        const newBrandCount = updatedCampaign?.campaignBrandCount ?? 0;
        decreaseEmailCount(newBrandCount);
        const newPitchCount = (pitchCount ?? 0) + newBrandCount;
        setPitchCount(newPitchCount);
        if (newPitchCount >= EMAIL_TARGET_FOR_FREE) {
          completeTask(OrganizationGamificationTask.send_pitch);
        }
        const hasFollowupTemplate =
          (updatedCampaign?.emailSetting?.emailSettingTemplates?.length ?? 0) >
          1;
        if (hasFollowupTemplate) {
          completeTask(OrganizationGamificationTask.send_followup);
        }
      }
      setCampaign(updatedCampaign);
      setCampaigns((prev) => {
        if (!prev) return [updatedCampaign];
        const existingIndex = prev.findIndex(
          (c) => c.id === updatedCampaign.id,
        );
        if (existingIndex !== -1) {
          const next = [...prev];
          next[existingIndex] = updatedCampaign;
          return next;
        }
        return [...prev, updatedCampaign];
      });
      if (errorMessage) setErrorMessage("");
      // On the create transition (/new -> /campaign/:id) replace history so Back
      // never lands on a stale /new with cleared state.
      if (!skipMoveNext) moveNext(updatedCampaign?.id, wasCreate);
      return true;
    } catch (error) {
      if (error?.message) setErrorMessage(error.message);
      setErrorAlert(error);
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!campaignId) {
      trackEvent("Campaign Builder Started", {
        "Entry Point": fromParam || "campaigns",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    trackEvent("Campaign Builder Step Viewed", {
      Step: CampaignStepTitles[CampaignStepOrders[activeStep]],
    });
  }, [activeStep]);

  useEffect(() => {
    if (topLevelCategories.length === 0) fetchBentoCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const dialogInitRef = useRef(false);
  useEffect(() => {
    // Dialog: either load a pre-created campaign and jump to a step (local pitching),
    // or start a fresh campaign in-session. Either way, run once — no URL to react to.
    if (dialogMode) {
      if (dialogInitRef.current || !currentOrg?.id) return;
      dialogInitRef.current = true;
      if (initialCampaignId) {
        loadDialogCampaign(
          initialCampaignId,
          initialStep ?? CampaignStep.details,
        );
      } else {
        resetBuilderState();
      }
      return;
    }
    if (currentOrg?.id) fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, currentOrg?.id]);

  const noMessagesRemaining =
    subscription?.remainingMessages !== undefined &&
    subscription.remainingMessages <= 0 &&
    (!campaign?.status || campaign?.status === CampaignStatus.draft);

  useEffect(() => {
    if (noMessagesRemaining) {
      setUpgradeDialogSource(UpgradeDialogSource.campaigns_limit);
      handleClose(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noMessagesRemaining]);

  if (noMessagesRemaining) return null;

  if (fetchingCampaign) {
    return (
      <Box sx={styles.page}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 12,
          }}
        >
          <CircularProgress size={36} />
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Loading campaign…
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.page}>
      <Header
        campaignName={campaign?.name}
        onBack={() => handleClose(false)}
        onRename={
          campaign?.id
            ? async (name) => {
                await handleUpdateCampaign({ name }, true);
              }
            : undefined
        }
        hideClose={dialogMode}
      />

      <Stepper activeStep={activeStep} alternativeLabel sx={styles.stepper}>
        {CampaignStepOrders.map((step, index) => {
          const isCompleted = index < activeStep;
          return (
            <Step
              key={step}
              completed={isCompleted}
              sx={{ cursor: isCompleted ? "pointer" : "default" }}
              onClick={() => handleStepClick(index)}
            >
              <StepLabel>{CampaignStepTitles[step]}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Box sx={styles.stepContent}>{renderStep()}</Box>
    </Box>
  );

  function renderStep() {
    switch (CampaignStepOrders[activeStep]) {
      case CampaignStep.select_brands:
        return (
          <Brands
            handleUpdateCampaign={handleUpdateCampaign}
            moveBack={moveBack}
            moveNext={moveNext}
            submitLoading={submitLoading}
            errorMessage={errorMessage}
            browseOnly={dialogMode}
          />
        );
      case CampaignStep.write_email:
        return (
          <WriteEmails
            handleUpdateCampaign={handleUpdateCampaign}
            moveBack={moveBack}
            submitLoading={submitLoading}
            templateError={templateError}
            onClearTemplateError={() => setTemplateError(null)}
          />
        );
      case CampaignStep.review:
        return (
          <Review
            handleUpdateCampaign={handleUpdateCampaign}
            moveBack={moveBack}
            onEditBrands={() =>
              navigateToStep(CampaignStep.select_brands, campaign?.id)
            }
            onEditTemplates={() =>
              navigateToStep(CampaignStep.write_email, campaign?.id)
            }
            submitLoading={submitLoading}
            errorMessage={errorMessage}
          />
        );
      default:
        return (
          <Details
            handleUpdateCampaign={handleUpdateCampaign}
            moveBack={moveBack}
            submitLoading={submitLoading}
          />
        );
    }
  }
}
